/**
 * Crypto wallets, shared by the client and the server. A wallet is only ever an identity here: a
 * scout links one by signing a one-time message, and can later sign in with it on a new device.
 * No transaction is ever asked for and nothing moves on any chain (see TOKENOMICS.md, Phase B).
 */
export const WALLET_CHAINS = ["evm", "solana"] as const;
export type WalletChain = (typeof WALLET_CHAINS)[number];

/** A challenge stays signable this long. */
export const WALLET_CHALLENGE_MS = 5 * 60_000;

export interface WalletLink {
  chain: WalletChain;
  /** EVM: lowercase 0x + 40 hex. Solana: base58 public key. */
  address: string;
  linkedAt: number;
}

export function isChain(v: unknown): v is WalletChain {
  return typeof v === "string" && (WALLET_CHAINS as readonly string[]).includes(v);
}

const B58 = "123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz";

export function base58Encode(bytes: Uint8Array): string {
  let zeros = 0;
  while (zeros < bytes.length && bytes[zeros] === 0) zeros++;
  const digits: number[] = [];
  for (let i = zeros; i < bytes.length; i++) {
    let carry = bytes[i]!;
    for (let j = 0; j < digits.length; j++) {
      carry += digits[j]! << 8;
      digits[j] = carry % 58;
      carry = (carry / 58) | 0;
    }
    while (carry) {
      digits.push(carry % 58);
      carry = (carry / 58) | 0;
    }
  }
  return "1".repeat(zeros) + digits.reverse().map((d) => B58[d]).join("");
}

export function base58Decode(s: string): Uint8Array | null {
  if (!/^[1-9A-HJ-NP-Za-km-z]*$/.test(s)) return null;
  let zeros = 0;
  while (zeros < s.length && s[zeros] === "1") zeros++;
  const bytes: number[] = [];
  for (let i = zeros; i < s.length; i++) {
    let carry = B58.indexOf(s[i]!);
    for (let j = 0; j < bytes.length; j++) {
      carry += bytes[j]! * 58;
      bytes[j] = carry & 0xff;
      carry >>= 8;
    }
    while (carry) {
      bytes.push(carry & 0xff);
      carry >>= 8;
    }
  }
  return new Uint8Array([...new Array(zeros).fill(0), ...bytes.reverse()]);
}

export function hexToBytes(hex: string): Uint8Array | null {
  const h = hex.replace(/^0x/i, "");
  if (h.length % 2 || !/^[0-9a-f]*$/i.test(h)) return null;
  const out = new Uint8Array(h.length / 2);
  for (let i = 0; i < out.length; i++) out[i] = parseInt(h.slice(i * 2, i * 2 + 2), 16);
  return out;
}

export function bytesToHex(b: Uint8Array): string {
  return [...b].map((x) => x.toString(16).padStart(2, "0")).join("");
}

/** The canonical form of an address, or null when it cannot be one on that chain. */
export function normalizeAddress(chain: WalletChain, address: unknown): string | null {
  if (typeof address !== "string") return null;
  const a = address.trim();
  if (chain === "evm") return /^0x[0-9a-fA-F]{40}$/.test(a) ? a.toLowerCase() : null;
  const raw = a.length <= 44 ? base58Decode(a) : null;
  return raw && raw.length === 32 ? a : null;
}

/** "0x12ab..9f3c" / "7xKX..sAsU": enough to recognise, short enough (and ASCII) for the pixel font. */
export function shortAddress(address: string): string {
  return address.length > 12 ? `${address.slice(0, address.startsWith("0x") ? 6 : 4)}..${address.slice(-4)}` : address;
}

export function chainLabel(chain: WalletChain): string {
  return chain === "evm" ? "Ethereum" : "Solana";
}

/**
 * The exact text a wallet signs, in the Sign-In with Ethereum (EIP-4361) / Solana (SIWS) layout that
 * phone wallets parse: domain first, then the address (EVM: pass it EIP-55 checksummed), a statement,
 * and the chain, nonce and expiry that make it single-use.
 */
export function walletMessage(p: {
  domain: string;
  chain: WalletChain;
  address: string;
  purpose: "link" | "login";
  nonce: string;
  issuedAt: number;
  expiresAt: number;
  scout?: string;
}): string {
  const statement = p.purpose === "link"
    ? `Link this wallet to the Pocket Meme scout ${p.scout ?? ""}. It proves you own the wallet and lets you sign in with it. No transaction, no fee, nothing leaves your wallet.`
    : "Sign in to Pocket Meme with this wallet. No transaction, no fee, nothing leaves your wallet.";
  return [
    `${p.domain} wants you to sign in with your ${chainLabel(p.chain)} account:`,
    p.address,
    "",
    statement.replace(/ +/g, " ").trim(),
    "",
    `URI: https://${p.domain}`,
    "Version: 1",
    `Chain ID: ${p.chain === "evm" ? "1" : "mainnet"}`,
    `Nonce: ${p.nonce}`,
    `Issued At: ${new Date(p.issuedAt).toISOString()}`,
    `Expiration Time: ${new Date(p.expiresAt).toISOString()}`,
  ].join("\n");
}
